import { auth } from '../firebase';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

export interface FavouriteCharacterImage {
  id: string;
  url: string;
  thumbnailUrl: string;
  createdAt: number;
  slotIndex?: number;  // 0, 1, or 2 for AI-generated images
}

export type ImageSource = 'ai' | 'custom';

export interface FavouriteCharacter {
  name: string | null;
  images: FavouriteCharacterImage[];
  imageSource: ImageSource;
  updatedAt: number | null;
  updatedBy: string | null;
}

export interface AIGenerateImagesResponse {
  images: FavouriteCharacterImage[];
  message: string;
}

export interface AIGenerateSingleImageResponse {
  image: FavouriteCharacterImage;
  slot_index: number;
  message: string;
}

export interface GenerateActionAIImageRequest {
  action_id: string;
  action_name: string;
  action_description: string;
  prompt_hint?: string;
}

export interface GenerateActionAIImageResponse {
  picture_url: string;
  thumbnail_url: string;
  message: string;
}

class FavouriteCharacterService {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const token = await user.getIdToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    return response.json();
  }

  /**
   * Get favourite character configuration for a device
   */
  async getFavouriteCharacter(deviceId: string): Promise<FavouriteCharacter> {
    return this.request<FavouriteCharacter>(`/devices/${deviceId}/favourite-character/`);
  }

  /**
   * Update favourite character name and/or image source
   */
  async updateFavouriteCharacter(
    deviceId: string,
    name: string | null,
    imageSource?: ImageSource
  ): Promise<FavouriteCharacter> {
    const body: { name: string | null; image_source?: ImageSource } = { name };
    if (imageSource !== undefined) {
      body.image_source = imageSource;
    }
    return this.request<FavouriteCharacter>(`/devices/${deviceId}/favourite-character/`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  /**
   * Upload an image for the favourite character
   */
  async uploadImage(deviceId: string, image: File): Promise<FavouriteCharacter> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const token = await user.getIdToken();

    const formData = new FormData();
    formData.append('image', image);

    const response = await fetch(`${API_BASE_URL}/devices/${deviceId}/favourite-character/images`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }

    return response.json();
  }

  /**
   * Delete an image from the favourite character
   */
  async deleteImage(deviceId: string, imageId: string): Promise<FavouriteCharacter> {
    return this.request<FavouriteCharacter>(`/devices/${deviceId}/favourite-character/images/${imageId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Generate AI images for the favourite character
   */
  async generateAIImages(deviceId: string, characterName: string): Promise<AIGenerateImagesResponse> {
    return this.request<AIGenerateImagesResponse>(`/devices/${deviceId}/favourite-character/generate-ai-images`, {
      method: 'POST',
      body: JSON.stringify({ character_name: characterName }),
    });
  }

  /**
   * Generate a single AI image for a specific slot
   */
  async generateSingleAIImage(
    deviceId: string,
    characterName: string,
    slotIndex: number
  ): Promise<AIGenerateSingleImageResponse> {
    return this.request<AIGenerateSingleImageResponse>(
      `/devices/${deviceId}/favourite-character/generate-single-ai-image`,
      {
        method: 'POST',
        body: JSON.stringify({ character_name: characterName, slot_index: slotIndex }),
      }
    );
  }

  /**
   * Generate an AI image for an action based on the favourite character
   */
  async generateActionAIImage(
    deviceId: string,
    actionId: string,
    actionName: string,
    actionDescription: string,
    promptHint?: string
  ): Promise<GenerateActionAIImageResponse> {
    const body: GenerateActionAIImageRequest = {
      action_id: actionId,
      action_name: actionName,
      action_description: actionDescription,
    };
    if (promptHint) {
      body.prompt_hint = promptHint;
    }
    return this.request<GenerateActionAIImageResponse>(
      `/devices/${deviceId}/favourite-character/generate-action-ai-image`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
  }
}

export const favouriteCharacterService = new FavouriteCharacterService();
